import { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiEye,
  FiFilter,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiShoppingBag,
  FiTruck,
  FiXCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

const statusLabels = {
  DRAFT: "Draft",
  CONFIRMED: "Confirmed",
  PARTIALLY_DELIVERED: "Partially Delivered",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
};

export default function SalesOrderList() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [storeId, setStoreId] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [actionOrderId, setActionOrderId] = useState(null);
  const [error, setError] = useState("");

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest("/sales-orders", {
        accessToken,
      });

      setOrders(response);
    } catch (requestError) {
      setError(requestError.message || "Unable to load sales orders.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      loadOrders();
    }
  }, [accessToken]);

  const stores = useMemo(
    () =>
      Array.from(
        new Map(
          orders
            .filter((order) => order.store)
            .map((order) => [order.store.id, order.store]),
        ).values(),
      ).sort((first, second) => first.name.localeCompare(second.name)),
    [orders],
  );

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();

    return orders.filter((order) => {
      const matchesSearch =
        !query ||
        [
          order.salesOrderNumber,
          order.customer?.fullName,
          order.customer?.phone,
          order.store?.name,
          order.createdBy?.employee?.fullName,
          order.createdBy?.email,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(query));

      const matchesStatus = status === "All" || order.status === status;

      const matchesStore = storeId === "All" || order.storeId === storeId;

      return matchesSearch && matchesStatus && matchesStore;
    });
  }, [orders, search, status, storeId]);

  const stats = useMemo(
    () => ({
      total: orders.length,
      drafts: orders.filter((order) => order.status === "DRAFT").length,
      ready: orders.filter((order) =>
        ["CONFIRMED", "PARTIALLY_DELIVERED"].includes(order.status),
      ).length,
      delivered: orders.filter((order) => order.status === "DELIVERED").length,
    }),
    [orders],
  );

  const confirmOrder = async (order) => {
    try {
      setActionOrderId(order.id);
      setError("");

      await apiRequest(`/sales-orders/${order.id}/confirm`, {
        method: "POST",
        accessToken,
      });

      await loadOrders();
    } catch (requestError) {
      setError(requestError.message || "Unable to confirm sales order.");
    } finally {
      setActionOrderId(null);
    }
  };

  const cancelOrder = async (order) => {
    const reason = window.prompt(
      `Cancel ${order.salesOrderNumber}? Enter a cancellation reason:`,
    );

    if (!reason?.trim()) return;

    try {
      setActionOrderId(order.id);
      setError("");

      await apiRequest(`/sales-orders/${order.id}/cancel`, {
        method: "POST",
        accessToken,
        body: { reason: reason.trim() },
      });

      await loadOrders();
    } catch (requestError) {
      setError(requestError.message || "Unable to cancel sales order.");
    } finally {
      setActionOrderId(null);
    }
  };

  const exportOrders = () => {
    const rows = [
      [
        "Sales Order No.",
        "Date",
        "Customer",
        "Store",
        "Items",
        "Quantity",
        "Amount",
        "Status",
      ],
      ...filteredOrders.map((order) => [
        order.salesOrderNumber,
        formatDate(order.orderDate),
        order.customer?.fullName || "",
        order.store?.name || "",
        order._count?.items || 0,
        (order.items || []).reduce(
          (total, item) => total + Number(item.quantityOrdered || 0),
          0,
        ),
        Number(order.totalAmount || 0),
        statusLabels[order.status] || order.status,
      ]),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    link.download = `sales-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
        <div>
          <h1 className="text-4xl font-bold">Sales Orders</h1>

          <p className="text-gray-500 mt-2">
            Manage all customer sales orders.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={exportOrders}
            disabled={!filteredOrders.length}
            className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2 disabled:opacity-50"
          >
            <FiDownload />
            Export
          </button>

          <button
            onClick={() => navigate("/admindashboard/sales/orders/create")}
            className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2"
          >
            <FiPlus />
            Create Sales Order
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Total Orders"
          value={stats.total}
          icon={<FiShoppingBag size={24} />}
          color="bg-blue-100 text-blue-700"
        />

        <StatCard
          title="Draft Orders"
          value={stats.drafts}
          icon={<FiClock size={24} />}
          color="bg-orange-100 text-orange-700"
        />

        <StatCard
          title="Ready For Delivery"
          value={stats.ready}
          icon={<FiTruck size={24} />}
          color="bg-purple-100 text-purple-700"
        />

        <StatCard
          title="Delivered"
          value={stats.delivered}
          icon={<FiCheckCircle size={24} />}
          color="bg-green-100 text-green-700"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6">
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search order, customer, phone..."
              className="w-full h-12 rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4 bg-white outline-none focus:border-primary-700"
          >
            <option value="All">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="PARTIALLY_DELIVERED">Partially Delivered</option>
            <option value="DELIVERED">Delivered</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4 bg-white outline-none focus:border-primary-700"
          >
            <option value="All">All Stores</option>

            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearch("");
              setStatus("All");
              setStoreId("All");
            }}
            className="h-12 rounded-xl bg-gray-100 flex items-center justify-center gap-2 hover:bg-gray-200"
          >
            <FiFilter />
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center px-6 py-5 border-b">
          <div>
            <h2 className="text-2xl font-bold">Sales Orders</h2>

            <p className="text-gray-500 mt-1">
              Showing {filteredOrders.length} of {orders.length} orders
            </p>
          </div>

          <button
            onClick={loadOrders}
            disabled={isLoading}
            className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2 disabled:opacity-60"
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1150px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-4 text-left">SO No</th>
                <th className="px-5 py-4 text-left">Date</th>
                <th className="px-5 py-4 text-left">Customer</th>
                <th className="px-5 py-4 text-left">Store</th>
                <th className="px-5 py-4 text-left">Executive</th>
                <th className="px-5 py-4 text-center">Items</th>
                <th className="px-5 py-4 text-center">Qty</th>
                <th className="px-5 py-4 text-right">Amount</th>
                <th className="px-5 py-4 text-center">Status</th>
                <th className="px-5 py-4 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="py-16 text-center text-gray-500">
                    Loading sales orders...
                  </td>
                </tr>
              ) : filteredOrders.length ? (
                filteredOrders.map((order) => {
                  const quantity = (order.items || []).reduce(
                    (total, item) => total + Number(item.quantityOrdered || 0),
                    0,
                  );

                  const isDraft = order.status === "DRAFT";
                  const canCancel = !["DELIVERED", "CANCELLED"].includes(
                    order.status,
                  );

                  return (
                    <tr
                      key={order.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-5 py-5">
                        <p className="font-semibold">
                          {order.salesOrderNumber}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          #{order.orderNumber}
                        </p>
                      </td>

                      <td className="px-5 py-5">
                        {formatDate(order.orderDate)}
                      </td>

                      <td className="px-5 py-5">
                        <p className="font-semibold">
                          {order.customer?.fullName || "—"}
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          {order.customer?.phone || "—"}
                        </p>
                      </td>

                      <td className="px-5 py-5">
                        <p className="font-medium">
                          {order.store?.name || "—"}
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          {order.store?.branch?.name || "—"}
                        </p>
                      </td>

                      <td className="px-5 py-5">
                        {order.createdBy?.employee?.fullName ||
                          order.createdBy?.email ||
                          "—"}
                      </td>

                      <td className="px-5 py-5 text-center">
                        {order._count?.items || 0}
                      </td>

                      <td className="px-5 py-5 text-center">{quantity}</td>

                      <td className="px-5 py-5 text-right font-bold">
                        {money(order.totalAmount)}
                      </td>

                      <td className="px-5 py-5 text-center">
                        <StatusBadge status={order.status} />
                      </td>

                      <td className="px-5 py-5">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() =>
                              navigate(
                                `/admindashboard/sales/orders/${order.id}`,
                              )
                            }
                            className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-primary-700 hover:text-white transition"
                            title="View order"
                          >
                            <FiEye />
                          </button>

                          {isDraft && (
                            <button
                              onClick={() => confirmOrder(order)}
                              disabled={actionOrderId === order.id}
                              className="h-9 px-3 rounded-lg border border-green-200 text-green-700 flex items-center gap-1 hover:bg-green-600 hover:text-white transition disabled:opacity-50"
                              title="Confirm and reserve stock"
                            >
                              <FiCheckCircle />
                            </button>
                          )}

                          {canCancel && (
                            <button
                              onClick={() => cancelOrder(order)}
                              disabled={actionOrderId === order.id}
                              className="h-9 w-9 rounded-lg border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition disabled:opacity-50"
                              title="Cancel order"
                            >
                              <FiXCircle />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="py-16 text-center text-gray-500">
                    No sales orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h2 className="text-3xl font-bold mt-3">{value}</h2>
        </div>

        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    DRAFT: "bg-gray-100 text-gray-700",
    CONFIRMED: "bg-blue-100 text-blue-700",
    PARTIALLY_DELIVERED: "bg-purple-100 text-purple-700",
    DELIVERED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || "bg-gray-100 text-gray-700"}`}
    >
      {statusLabels[status] || status}
    </span>
  );
}
